import { useCallback, useEffect, useState } from "react";
import { TextField } from "@mui/material";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import { createUseStyles } from "$utils/createUseStyles";
import { theme } from "$utils/theme/bataillog";
import LimitTag from "$components/LimitTag";
import JSelectDisplayOption from "$components/JSelectDisplayOption/JSelectDisplayOption";
import { ReceptionService } from "$services";

const autoCompleteOptions = createFilterOptions<any>();

/** Order.reference values linked to a reception (via reception_references → orders). */
export function orderReferencesFromReception(reception: any): string[] {
  if (!reception?.receptionReferences?.length) return [];
  const refs: string[] = reception.receptionReferences.flatMap((rr: any) =>
    (rr.orders || []).flatMap((ro: any) => {
      const ref = ro.order?.reference;
      return typeof ref === "string" && ref.length > 0 ? [ref] : [];
    })
  );
  return [...new Set(refs)];
}

export function orderReferencesFromReceptions(receptions: any[]): string[] {
  const all = receptions.flatMap(orderReferencesFromReception);
  return [...new Set(all)];
}

type Props = {
  label?: string;
  limitTags?: number;
  placeholder?: string;
  companyId?: string;
  value: any;
  multiple?: boolean;
  onChange: (event: any, data: any) => void;
  additionalFilters?: any;
};

function ReferencementSelect({
  label,
  limitTags = 2,
  placeholder,
  companyId,
  value,
  multiple,
  onChange,
  additionalFilters,
}: Props) {
  const classes = useStyles();
  const [referencementOrder, setReferencementOrder] = useState([]);

  const handleSearchReferencement = useCallback(
    () =>
      ReceptionService.getReferencements(companyId, {
        filters: additionalFilters,
      }).then((res) => {
        setReferencementOrder(res);
      }),
    [companyId, additionalFilters]
  );

  useEffect(() => {
    handleSearchReferencement();
  }, [handleSearchReferencement, companyId]);

  return (
    <Autocomplete
      onInputChange={() => handleSearchReferencement()}
      className={classes.select}
      limitTags={limitTags}
      getLimitTagsText={(more) => <LimitTag nbMore={more} />}
      options={referencementOrder}
      onChange={onChange}
      value={value}
      filterOptions={(options, params) => autoCompleteOptions(options, params)}
      multiple={multiple}
      getOptionLabel={(option: any) =>
        option.reference ?? (option.id != null ? `Réception #${option.id}` : "")
      }
      isOptionEqualToValue={(option, value) =>
        option?.id != null && value?.id != null
          ? option.id === value.id
          : option?.reference === value?.reference
      }
      renderOption={(props, option) => (
        <li {...props}>
          <JSelectDisplayOption
            value1={option.reference}
            value2={orderReferencesFromReception(option).join(", ")}
          />
        </li>
      )}
      noOptionsText="Pas de résultat"
      renderInput={(params: any) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          variant="outlined"
          style={{ width: "100%" }}
        />
      )}
    />
  );
}

const useStyles = createUseStyles(() => ({
  select: {
    flex: "1",
    paddingRight: "20px",
    "&:last-child": {
      padding: 0,
    },
    background: theme.color.white,
    "& .MuiInputLabel-shrink": {
      background: `${theme.color.white} !important`,
    },
  },
}));

export default ReferencementSelect;
